/*!
 * Timbrica MIDI Humanizer — application wiring.
 *
 * Flow: pick a .mid → parts are identified → the performance is computed
 * (deterministically) → the user hears A/B and sees the diff → exports MIDI or
 * WAV. Every recompute is cheap (tens of milliseconds on a symphony), so every
 * control is live rather than hidden behind a "render" button.
 */

import { load, cloneDoc, save, makeTickClock } from './smf.js';
import { perform } from './perform.js';
import { measure } from './metrics.js';
import { DiffRoll, familyColor } from './pianoroll.js';
import { loadInstruments, renderWav, liveVoices } from './audio.js';
import { FAMILY_ORDER } from './taxonomy.js';
import { loadScriptChain, soundfontBases } from './assets.js';

/**
 * How much audio the WAV export may render, in seconds.
 *
 * An OfflineAudioContext holds the ENTIRE result as float samples, and
 * encodeWav then allocates the 16-bit copy on top: about 12 bytes per stereo
 * frame, so six minutes at 44.1 kHz is ~190 MB live at once. A desktop shrugs;
 * an iPhone kills the tab, and a killed tab reports nothing — the user just
 * sees the app disappear.
 *
 * So the ceiling follows the device instead of being one number picked for the
 * machine the tool was written on. The MIDI export has no limit of any kind —
 * only the audio preview does — and whichever ceiling applies is stated on the
 * page rather than silently trimming the file.
 */
function renderLimitSec() {
    const BYTES_PER_FRAME = 12;         /* float render + 16-bit copy, stereo */
    const mem = (navigator && navigator.deviceMemory) || 0;
    const coarse = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    /* deviceMemory is Chromium-only; Safari never reports it, so a coarse
       pointer is what stands in for "this is a phone" there. */
    const budgetMb = mem ? Math.min(320, Math.max(64, mem * 40)) : (coarse ? 96 : 320);
    const sec = (budgetMb * 1024 * 1024) / (BYTES_PER_FRAME * 44100);
    return Math.max(60, Math.min(600, Math.round(sec / 30) * 30));
}
const RENDER_LIMIT_SEC = renderLimitSec();

const $ = id => document.getElementById(id);
const T = (key, fallback) => {
    const dict = window.__MH_I18N || {};
    return (dict[key] !== undefined) ? dict[key] : (fallback !== undefined ? fallback : key);
};

const state = {
    fileName: '',
    fileKey: null,
    original: null,       /* pristine doc */
    performed: null,      /* humanized doc */
    clock: null,
    before: null,
    after: null,
    instruments: null,
    ctx: null,
    playing: false,
    playSide: 'B',
    playStartCtxTime: 0,
    playStartSec: 0,
    live: null,           /* NoteRender.live player of the audition */
    raf: 0,
    roll: null,
    overrides: {},        /* trackIndex → { family, players } */
    soloTrack: null,      /* trackIndex being auditioned alone, or null */
    familyStrength: {},   /* family → multiplier on top of the global slider */
    loop: null,           /* {startSec, endSec} — the bars being compared */
    hadExistingCC: false,
    settings: {
        preset: 'orchestral', strength: 1.0, seed: 1,
        expressionMod: true, expressionExpr: true,
        libraryLatency: 'none', separateChannels: false,
    },
};

/* ── boot ─────────────────────────────────────────────────────────────────── */

function boot() {
    const zone = $('mhUpload');
    if (zone && window.__bindUploaderFile) window.__bindUploaderFile(zone, onFile);
    else if (zone) zone.addEventListener('tool-uploader:file', e => e.detail && e.detail.file && onFile(e.detail.file));
    if (zone) zone.addEventListener('tool-uploader:clear', reset);

    document.querySelectorAll('[data-preset]').forEach(btn => {
        btn.addEventListener('click', () => {
            state.settings.preset = btn.dataset.preset;
            document.querySelectorAll('[data-preset]').forEach(b => b.setAttribute('aria-pressed', String(b === btn)));
            saveSetting('preset', btn.dataset.preset);
            showPresetHint(btn.dataset.preset);
            recompute('preset');
        });
    });

    bindRange('mhStrength', 'strength', v => v / 100, v => Math.round(v * 100) + '%');
    bindSelect('mhLatency', 'libraryLatency');
    bindCheck('mhCcMod', 'expressionMod');
    bindCheck('mhCcExpr', 'expressionExpr');
    bindCheck('mhSepCh', 'separateChannels');

    const seed = $('mhSeed');
    if (seed) seed.addEventListener('click', () => {
        state.settings.seed = (state.settings.seed % 9999) + 1 + Math.floor(Math.random() * 7);
        recompute('seed');
        announce(T('another_take', 'Другой дубль'));
    });

    const ab = $('mhAB');
    if (ab) ab.addEventListener('click', () => setSide(state.playSide === 'B' ? 'A' : 'B'));

    const play = $('mhPlay');
    if (play) play.addEventListener('click', togglePlay);

    const loopClear = $('mhLoopClear');
    if (loopClear) loopClear.addEventListener('click', clearLoop);

    const dlMid = $('mhDownloadMidi');
    if (dlMid) dlMid.addEventListener('click', exportMidi);
    const dlWav = $('mhDownloadWav');
    if (dlWav) dlWav.addEventListener('click', exportWav);

    const zoom = $('mhZoom');
    if (zoom) zoom.addEventListener('input', () => {
        if (!state.roll) return;
        /* Zoom around the CENTRE of what is on screen: anchoring at the left
           edge throws away the passage the user was looking at. */
        const v = state.roll.view;
        const centre = v.startSec + v.spanSec / 2;
        const span = Math.pow(2, (+zoom.value) / 20) * 1.2;
        state.roll.setView(Math.max(0, centre - span / 2), span).draw();
        syncScrollSlider();
    });
    const scroll = $('mhScroll');
    if (scroll) scroll.addEventListener('input', () => {
        if (!state.roll || !state.performed) return;
        const max = Math.max(0.1, state.performed.durationSec - state.roll.view.spanSec);
        state.roll.setView((+scroll.value / 1000) * max, state.roll.view.spanSec).draw();
    });

    /* Space plays, B flips the comparison — the two things the ear needs on a
       loop. Ignored while typing, and space is only swallowed when we act on
       it, so the page never loses its normal scrolling. */
    document.addEventListener('keydown', e => {
        if (!state.performed || e.ctrlKey || e.metaKey || e.altKey) return;
        const el = document.activeElement;
        if (window.__keyOwnedByFocus ? window.__keyOwnedByFocus(e) : (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
        if (e.code === 'Space') { e.preventDefault(); togglePlay(); }
        else if ((window.__keyLetter ? window.__keyLetter(e) : String(e.key || '').toLowerCase()) === 'b') {
            e.preventDefault();
            setSide(state.playSide === 'B' ? 'A' : 'B');
        } else if (e.key === 'Escape' && state.loop) {
            e.preventDefault();
            clearLoop();
        }
    });

    window.addEventListener('resize', () => { if (state.roll) state.roll.resize().draw(); });
    if (window.__stage) window.__stage('idle');
}

function bindRange(id, key, map, fmt) {
    const el = $(id);
    if (!el) return;
    const out = $(id + 'Val');
    const apply = () => {
        state.settings[key] = map ? map(+el.value) : +el.value;
        if (out) out.textContent = fmt ? fmt(state.settings[key]) : String(state.settings[key]);
    };
    apply();
    el.addEventListener('input', () => { apply(); scheduleRecompute(key); });
    if (window.__toolSettings) window.__toolSettings.bind('midi-humanizer', el, key, el.value);
}
function bindSelect(id, key) {
    const el = $(id);
    if (!el) return;
    state.settings[key] = el.value;
    el.addEventListener('change', () => { state.settings[key] = el.value; recompute(key); });
    if (window.__toolSettings) window.__toolSettings.bind('midi-humanizer', el, key, el.value);
}
function bindCheck(id, key) {
    const el = $(id);
    if (!el) return;
    state.settings[key] = !!el.checked;
    el.addEventListener('change', () => { state.settings[key] = !!el.checked; recompute(key); });
    if (window.__toolSettings) window.__toolSettings.bind('midi-humanizer', el, key, el.checked);
}
/* The preset names alone do not say what changes — "Chamber group" against
   "Orchestra" is a guess unless the page says a chamber group is tighter and
   more freely phrased. One line, swapped with the choice. */
function showPresetHint(preset) {
    const el = $('mhPresetHint');
    if (el) el.textContent = T('hint_' + preset, '');
}
function saveSetting(k, v) {
    if (window.__toolSettings) window.__toolSettings.save('midi-humanizer', k, v);
}
/** Keep the position slider's thumb where the view actually is. */
function syncScrollSlider() {
    const scroll = $('mhScroll');
    if (!scroll || !state.roll || !state.performed) return;
    const max = Math.max(0.1, state.performed.durationSec - state.roll.view.spanSec);
    scroll.value = String(Math.round(Math.min(1, state.roll.view.startSec / max) * 1000));
}
function announce(msg) { if (window.__announce) window.__announce(msg); }

/* ── file in ──────────────────────────────────────────────────────────────── */

async function onFile(file) {
    if (!file) return;
    stopPlayback();
    /* A new score needs a new sample set: the instruments cached for the
       previous file cover the previous file's programs. Keeping them meant a
       part whose instrument was never loaded played silence, with no error
       anywhere — the page looked perfectly healthy. */
    state.instruments = null;
    state.everPlayed = false;
    state.playStartSec = 0;
    state.soloTrack = null;
    state.familyStrength = {};
    state.loop = null;
    if (state.roll) state.roll.setTrackFilter(null).setLoop(null);
    const loopBar = $('mhLoopBar');
    if (loopBar) loopBar.hidden = true;
    state.fileName = file.name || 'score.mid';
    state.fileKey = window.__toolLimit ? window.__toolLimit.fileKey(file) : null;
    if (window.__stage) window.__stage('file_picked');
    if (window.__aeFunnel) window.__aeFunnel('file_picked', { size: file.size });

    try {
        await ensureMidiLib();
        const buf = await file.arrayBuffer();
        const doc = load(window.Midi, buf);
        if (!doc.tracks.length) throw new Error('no_notes');

        state.original = doc;
        state.clock = makeTickClock(doc);
        state.before = measure(doc);
        state.overrides = {};

        /* Does this file already carry hand-drawn expression? Our CC1/CC11
           lanes REPLACE the track's own, so a mockup somebody spent an evening
           riding a mod wheel over would come back overwritten — the one
           unrecoverable thing this tool could do. When we find existing lanes
           we default to leaving them alone and say so; replacing stays one
           checkbox away. */
        const hadCC = doc.tracks.some(t => (t.cc['1'] || []).length > 2 || (t.cc['11'] || []).length > 2);
        state.hadExistingCC = hadCC;
        const ccWarn = $('mhCcWarn');
        if (ccWarn) ccWarn.hidden = !hadCC;
        if (hadCC) {
            const mod = $('mhCcMod'), expr = $('mhCcExpr');
            if (mod) { mod.checked = false; state.settings.expressionMod = false; }
            if (expr) { expr.checked = false; state.settings.expressionExpr = false; }
            const adv = document.querySelector('.mh-adv');
            if (adv) adv.open = true;
            if (window.__aeFunnel) window.__aeFunnel('existing_cc_preserved', {
                tracks: doc.tracks.filter(t => (t.cc['1'] || []).length > 2 || (t.cc['11'] || []).length > 2).length,
            });
        }

        renderParts();
        renderFamilyStrength();
        show('mhWorkspace');
        recompute('load');
        if (window.__stage) window.__stage('loaded');

        /* What kind of score did we get, and did we understand it? `unknown`
           counts the tracks the taxonomy could not place — that number is the
           quality of the classifier, and without it we would only ever learn
           about misreads from a bug report. */
        const fams = {};
        doc.tracks.forEach(t => { fams[t.part.family] = (fams[t.part.family] || 0) + 1; });
        if (window.__aeFunnel) window.__aeFunnel('score_loaded', {
            tracks: doc.tracks.length,
            notes: doc.tracks.reduce((s, t) => s + t.notes.length, 0),
            seconds: Math.round(doc.durationSec),
            ppq: doc.ppq,
            tempos: doc.tempos.length,
            families: Object.keys(fams).length,
            unknown: fams.other || 0,
            named: doc.tracks.filter(t => t.name).length,
            shared_channels: (state.lastLog && state.lastLog.sharedChannels || []).length,
            long: doc.durationSec > RENDER_LIMIT_SEC ? 1 : 0,
            /* notes @tonejs/midi alone would have read wrong (midi-stream-notes.js):
               zero-length strikes it made, and tracks the door re-read */
            read_fixed_tracks: (doc.readReport && doc.readReport.tracksRebuilt) || 0,
            read_zero_before: (doc.readReport && doc.readReport.zeroBefore) || 0,
            read_zero_after: (doc.readReport && doc.readReport.zeroAfter) || 0,
        });
        const longNote = $('mhLongWarn');
        if (longNote) {
            longNote.hidden = doc.durationSec <= RENDER_LIMIT_SEC;
            /* The ceiling is device-dependent, so the sentence has to carry the
               number rather than name one in prose. */
            longNote.textContent = T('long_warn', '').replace(':minutes', String(Math.round(RENDER_LIMIT_SEC / 60)));
        }
    } catch (err) {
        const msg = (err && err.message === 'no_notes')
            ? T('err_no_notes', 'В этом файле нет нот — возможно, это не MIDI-партитура.')
            : T('err_parse', 'Не удалось разобрать MIDI-файл.');
        if (window.__toast) window.__toast(msg, 'error');
        if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'parse_failed', err: String(err && err.message) });
    }
}

function assetChain(key, fallbackPath) {
    const a = (window.__MH_ASSETS || {})[key];
    if (Array.isArray(a) && a.length) return a;
    if (typeof a === 'string' && a) return [a];
    return [fallbackPath];
}

async function ensureMidiLib() {
    await loadScriptChain(assetChain('midiLib', '/vendor/midi/Midi.min.js'), 'midi-lib', () => !!window.Midi);
    if (!window.Midi) throw new Error('midi_lib_missing');
}
async function ensureSoundfontLib() {
    await loadScriptChain(assetChain('soundfont', '/vendor/soundfont/soundfont-player.min.js'),
        'soundfont-player', () => !!window.Soundfont);
    if (!window.Soundfont) throw new Error('soundfont_missing');
}

function reset() {
    stopPlayback();
    state.original = state.performed = state.before = state.after = null;
    state.instruments = null;
    state.everPlayed = false;
    state.playStartSec = 0;
    state.overrides = {};
    state.familyStrength = {};
    hide('mhWorkspace');
}

/* ── compute ──────────────────────────────────────────────────────────────── */

let recomputeTimer = 0;
let reArmTimer = 0;
let reportTimer = 0;
let lastComputeMs = 0;

/**
 * Debounce that follows the score, not the clock.
 *
 * Measured on generated scores: 32 000 notes recompute in ~215 ms end to end,
 * 320 000 in ~1 s. A fixed 90 ms debounce turns a slider drag on a big file
 * into a chain of one-second freezes — the tool feels broken exactly on the
 * scores that need it most. So the wait grows with what the last pass actually
 * cost, and the expensive half (the measurement pass behind the report) is
 * deferred out of the drag entirely: dragging updates the performance and the
 * picture, the numbers land when the hand stops.
 */
function scheduleRecompute(reason) {
    clearTimeout(recomputeTimer);
    const wait = Math.min(600, Math.max(90, Math.round(lastComputeMs * 1.2)));
    recomputeTimer = setTimeout(() => recompute(reason, true), wait);
}

/**
 * Make what is PLAYING follow the controls.
 *
 * Without this, moving the "how alive" slider changed the picture and the
 * report while the ear kept hearing the previous take until you pressed stop
 * and play again — on a tool whose entire proposition is "listen to the
 * difference". Re-scheduling happens only after the drag has settled (a restart
 * on every 90 ms recompute would stutter), and only when the performed side is
 * the one being auditioned; the "as written" side never changes.
 */
function reArmPlayback() {
    clearTimeout(reArmTimer);
    if (!state.playing || state.playSide !== 'B') return;
    reArmTimer = setTimeout(() => {
        if (!state.playing) return;
        const at = currentSec();
        stopSources();
        startAt(at);
    }, 320);
}

function recompute(reason, light) {
    if (!state.original) return;
    const t0 = (performance && performance.now) ? performance.now() : 0;

    const doc = cloneDoc(state.original);
    /* per-track overrides from the parts table */
    doc.tracks.forEach(t => {
        const ov = state.overrides[t.index];
        if (!ov) return;
        if (ov.players) t.part.players = ov.players;
        if (ov.family) {
            t.part.family = ov.family;
            t.part.canSwell = ov.family === 'strings' || ov.family === 'brass' || ov.family === 'woodwind' || ov.family === 'choir';
            t.part.legato = t.part.canSwell;
        }
    });

    const s = state.settings;
    const res = perform(doc, {
        preset: s.preset,
        strength: s.strength,
        seed: s.seed,
        expressionCC: { mod: s.expressionMod, expr: s.expressionExpr },
        familyStrength: state.familyStrength,
        libraryLatency: s.libraryLatency === 'none' ? null : s.libraryLatency,
        separateChannels: s.separateChannels,
    });

    state.performed = doc;
    state.lastLog = res.log;

    /* The report costs as much again as the performance itself on a large
       score, and nobody reads numbers mid-drag. */
    if (!light) {
        state.after = measure(doc);
        renderReport(((performance && performance.now) ? performance.now() : 0) - t0);
    } else {
        clearTimeout(reportTimer);
        reportTimer = setTimeout(() => {
            if (!state.performed) return;
            const rt0 = (performance && performance.now) ? performance.now() : 0;
            state.after = measure(state.performed);
            renderReport(((performance && performance.now) ? performance.now() : 0) - rt0);
        }, 260);
    }
    const ms = ((performance && performance.now) ? performance.now() : 0) - t0;
    lastComputeMs = ms;
    reArmPlayback();
    if (!state.roll) {
        const canvas = $('mhRoll');
        if (canvas) {
            state.roll = new DiffRoll(canvas);
            wireRollPointer(canvas);
        }
    }
    if (state.roll) {
        state.roll.setData(state.original, state.performed, state.clock).resize();
        const zoom = $('mhZoom');
        const span = zoom ? Math.pow(2, (+zoom.value) / 20) * 1.2 : 8;
        state.roll.setView(state.roll.view.startSec, span).draw();
    }
    if (window.__aeFunnel) window.__aeFunnel('recomputed', { reason, ms: Math.round(ms), preset: s.preset });
}

/**
 * Pointer on the roll: a TAP seeks, a DRAG marks the bars to loop.
 *
 * Comparing a performance is a loop, not a listen-through: the same four bars,
 * one side then the other, until the ear decides. Without a loop the user
 * re-aims the playhead by hand on every pass, which is enough friction that
 * most people compare once and give up.
 *
 * Pointer events (not mouse) so a finger works the same as a mouse; the 8 px
 * threshold is what separates "I meant to jump here" from "I meant this range",
 * and it has to exist because a tap on a touchscreen always moves a little.
 */
function wireRollPointer(canvas) {
    let downX = null, downSec = 0, dragging = false, pointerId = null;

    canvas.addEventListener('pointerdown', e => {
        if (!state.roll || !state.performed) return;
        downX = e.clientX;
        downSec = state.roll.secAt(e.clientX);
        dragging = false;
        pointerId = e.pointerId;
        try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
    });

    canvas.addEventListener('pointermove', e => {
        if (downX === null || e.pointerId !== pointerId) return;
        if (!dragging && Math.abs(e.clientX - downX) < 8) return;
        dragging = true;
        const a = Math.min(downSec, state.roll.secAt(e.clientX));
        const b = Math.max(downSec, state.roll.secAt(e.clientX));
        state.roll.setLoop({ startSec: Math.max(0, a), endSec: b }).draw();
    });

    const finish = e => {
        if (downX === null || (pointerId !== null && e.pointerId !== pointerId)) return;
        try { canvas.releasePointerCapture(pointerId); } catch (_) {}
        if (dragging) {
            const a = Math.min(downSec, state.roll.secAt(e.clientX));
            const b = Math.max(downSec, state.roll.secAt(e.clientX));
            /* A sliver is a mis-drag, not a loop anyone wants to hear. */
            if (b - a >= 0.35) setLoop({ startSec: Math.max(0, a), endSec: b });
            else clearLoop();
        } else {
            seekTo(downSec);
        }
        downX = null; pointerId = null; dragging = false;
    };
    canvas.addEventListener('pointerup', finish);
    canvas.addEventListener('pointercancel', () => { downX = null; pointerId = null; dragging = false; });
}

/**
 * Snap a loop to the beat.
 *
 * A loop that starts a fraction of a beat late sounds wrong precisely where it
 * matters — at the wrap, every single pass. Nobody can place a drag to the
 * millisecond, and nobody should have to: the tempo map already says where the
 * beats are. Snapping is skipped when the range is shorter than one beat, which
 * is the one case where the user is clearly picking something finer than the
 * grid on purpose.
 */
function snapToBeat(loop) {
    if (!state.clock || !state.clock.bpmAt) return loop;
    const bpm = state.clock.bpmAt(0) || 120;
    const beat = 60 / bpm;
    if (!isFinite(beat) || beat <= 0) return loop;
    if (loop.endSec - loop.startSec < beat) return loop;
    const a = Math.max(0, Math.round(loop.startSec / beat) * beat);
    let b = Math.round(loop.endSec / beat) * beat;
    if (b - a < beat) b = a + beat;
    return { startSec: a, endSec: b, snapped: true };
}

function setLoop(raw) {
    const loop = snapToBeat(raw);
    state.loop = loop;
    if (state.roll) state.roll.setLoop(loop).draw();
    const bar = $('mhLoopBar');
    if (bar) bar.hidden = false;
    const label = $('mhLoopLabel');
    /* m:ss cannot describe a four-bar loop — "0:00 — 0:01" is what the first
       version showed. The length is the number the ear cares about. */
    if (label) label.textContent = T('loop_range', 'Повтор: :a — :b · :len')
        .replace(':a', fmtTime(loop.startSec))
        .replace(':b', fmtTime(loop.endSec))
        .replace(':len', fmtDur(loop.endSec - loop.startSec));
    if (window.__aeFunnel) window.__aeFunnel('loop_set', { seconds: Math.round(loop.endSec - loop.startSec) });
    /* Jump into the loop straight away — the point is to hear it. */
    if (state.playing) { stopSources(); startAt(loop.startSec); }
    else state.playStartSec = loop.startSec;
}

function clearLoop() {
    if (!state.loop) return;
    state.loop = null;
    if (state.roll) state.roll.setLoop(null).draw();
    const bar = $('mhLoopBar');
    if (bar) bar.hidden = true;
    if (state.playing) { const at = currentSec(); stopSources(); startAt(at); }
}

/* ── parts table ──────────────────────────────────────────────────────────── */

function renderParts() {
    const host = $('mhParts');
    if (!host || !state.original) return;
    host.innerHTML = '';

    state.original.tracks.forEach(t => {
        const row = document.createElement('div');
        row.className = 'mh-part';
        row.style.setProperty('--part-color', familyColor(t.part.family, 1));

        const dot = document.createElement('span');
        dot.className = 'mh-part-dot';
        row.appendChild(dot);

        const name = document.createElement('span');
        name.className = 'mh-part-name';
        name.textContent = t.name || (T('program', 'Программа') + ' ' + t.program);
        name.title = `${t.notes.length} ${T('notes_short', 'нот')}`;
        row.appendChild(name);

        const fam = document.createElement('select');
        fam.className = 'mh-part-family lg-input';
        fam.setAttribute('aria-label', T('section', 'Секция'));
        FAMILY_ORDER.forEach(f => {
            const opt = document.createElement('option');
            opt.value = f;
            opt.textContent = T('family_' + f, f);
            if (f === t.part.family) opt.selected = true;
            fam.appendChild(opt);
        });
        fam.addEventListener('change', () => {
            state.overrides[t.index] = Object.assign({}, state.overrides[t.index], { family: fam.value });
            row.style.setProperty('--part-color', familyColor(fam.value, 1));
            recompute('part_family');
        });
        row.appendChild(fam);

        const players = document.createElement('input');
        players.type = 'number'; players.min = '1'; players.max = '40';
        players.value = String(t.part.players);
        players.className = 'mh-part-players lg-input';
        players.setAttribute('aria-label', T('players', 'Музыкантов'));
        players.addEventListener('change', () => {
            const v = Math.max(1, Math.min(40, parseInt(players.value, 10) || 1));
            players.value = String(v);
            state.overrides[t.index] = Object.assign({}, state.overrides[t.index], { players: v });
            recompute('part_players');
        });
        row.appendChild(players);

        const solo = document.createElement('button');
        solo.type = 'button';
        solo.className = 'mh-part-focus';
        solo.textContent = T('focus', 'Показать');
        solo.setAttribute('aria-pressed', 'false');
        solo.addEventListener('click', () => {
            const on = solo.getAttribute('aria-pressed') !== 'true';
            document.querySelectorAll('.mh-part-focus').forEach(b => b.setAttribute('aria-pressed', 'false'));
            solo.setAttribute('aria-pressed', String(on));
            state.soloTrack = on ? t.index : null;
            if (state.roll) state.roll.setTrackFilter(state.soloTrack).draw();
            /* Solo is an EAR tool first. schedule() has always taken a mute set;
               nothing passed one, so the button dimmed the diff and left the
               full ensemble playing — which is the opposite of what "listen to
               this part" means. */
            if (state.playing) { const at = currentSec(); stopSources(); startAt(at); }
            if (window.__aeFunnel) window.__aeFunnel('solo', { on: on ? 1 : 0, family: t.part.family });
        });
        row.appendChild(solo);

        host.appendChild(row);
    });
}

/**
 * Per-section strength.
 *
 * "Let the strings breathe, keep the brass tight" is an ordinary thing to ask
 * in a session and impossible to say with one slider. Sliders are drawn only
 * for the families the SCORE actually contains — a fixed row of eight would be
 * mostly dead controls, and a dead control teaches the user that the panel is
 * decoration.
 */
function renderFamilyStrength() {
    const host = $('mhFamilies');
    if (!host || !state.original) return;
    host.innerHTML = '';

    const present = [];
    const seen = new Set();
    FAMILY_ORDER.forEach(f => {
        if (state.original.tracks.some(t => t.part.family === f) && !seen.has(f)) { seen.add(f); present.push(f); }
    });
    /* One family is the whole score — the global slider already says it. */
    const wrap = $('mhFamiliesWrap');
    if (wrap) wrap.hidden = present.length < 2;
    if (present.length < 2) { state.familyStrength = {}; return; }

    present.forEach(fam => {
        const row = document.createElement('div');
        row.className = 'mh-row mh-fam-row';

        const label = document.createElement('label');
        const id = 'mhFam_' + fam;
        label.setAttribute('for', id);
        label.textContent = T('family_' + fam, fam);
        label.style.setProperty('--part-color', familyColor(fam, 1));
        label.className = 'mh-fam-label';
        row.appendChild(label);

        const input = document.createElement('input');
        input.type = 'range'; input.id = id;
        input.min = '0'; input.max = '150'; input.step = '10';
        input.value = String(Math.round((state.familyStrength[fam] !== undefined ? state.familyStrength[fam] : 1) * 100));
        row.appendChild(input);

        const out = document.createElement('span');
        out.className = 'mh-val';
        out.textContent = input.value + '%';
        row.appendChild(out);

        input.addEventListener('input', () => {
            const v = (+input.value) / 100;
            state.familyStrength[fam] = v;
            out.textContent = input.value + '%';
            scheduleRecompute('family_strength');
        });
        input.addEventListener('change', () => {
            if (window.__aeFunnel) window.__aeFunnel('family_strength', { family: fam, value: Math.round((+input.value)) });
        });

        host.appendChild(row);
    });
}

/* ── report ───────────────────────────────────────────────────────────────── */

const REPORT_ROWS = [
    { key: 'gridLockPct', unit: '%', better: 'down', label: 'm_gridlock' },
    { key: 'ensembleSpreadMs', unit: ' ms', better: 'up', label: 'm_ensemble' },
    { key: 'expressionCoveragePct', unit: '%', better: 'up', label: 'm_expression' },
    { key: 'legatoOverlapMs', unit: ' ms', better: 'up', label: 'm_legato' },
    { key: 'microtimingMs', unit: ' ms', better: 'up', label: 'm_microtiming' },
    { key: 'velocityStdev', unit: '', better: 'up', label: 'm_velocity' },
];

function renderReport(computeMs) {
    const host = $('mhReport');
    if (!host || !state.before || !state.after) return;
    host.innerHTML = '';

    REPORT_ROWS.forEach(r => {
        const b = state.before[r.key], a = state.after[r.key];
        const cell = document.createElement('div');
        cell.className = 'mh-metric';
        const moved = Math.abs(a - b) > 0.05;
        const good = r.better === 'up' ? a > b : a < b;
        cell.innerHTML =
            `<div class="mh-metric-label">${escapeHtml(T(r.label, r.key))}</div>` +
            `<div class="mh-metric-val${moved && good ? ' is-good' : ''}">` +
            `<span class="mh-metric-before">${fmtNum(b)}${r.unit}</span>` +
            `<span class="mh-metric-arrow" aria-hidden="true">→</span>` +
            `<span class="mh-metric-after">${fmtNum(a)}${r.unit}</span></div>`;
        host.appendChild(cell);
    });

    const note = $('mhReportNote');
    if (note && state.lastLog) {
        const bits = [];
        bits.push(T('r_notes', 'Нот') + ': ' + state.after.notes);
        bits.push(T('r_phrases', 'Фраз') + ': ' + state.lastLog.phrases);
        bits.push(T('r_cc', 'Точек CC') + ': ' + state.lastLog.ccWritten);
        if (state.lastLog.overlapsFixed) bits.push(T('r_overlaps', 'Исправлено наложений') + ': ' + state.lastLog.overlapsFixed);
        if (state.lastLog.channelsReassigned) bits.push(T('r_channels', 'Каналов разнесено') + ': ' + state.lastLog.channelsReassigned);
        bits.push(Math.round(computeMs) + ' ms');
        note.textContent = bits.join(' · ');
    }
    const warn = $('mhShareWarn');
    if (warn) {
        const shared = state.lastLog && state.lastLog.sharedChannels && state.lastLog.sharedChannels.length;
        warn.hidden = !shared || state.settings.separateChannels;
    }
}

function fmtNum(v) {
    if (typeof v !== 'number' || !isFinite(v)) return '—';
    return Math.abs(v) >= 100 ? v.toFixed(0) : Math.abs(v) >= 10 ? v.toFixed(1) : v.toFixed(2);
}
function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/* ── playback ─────────────────────────────────────────────────────────────── */

function setSide(side) {
    state.playSide = side;
    const ab = $('mhAB');
    if (ab) {
        ab.setAttribute('data-side', side);
        ab.textContent = side === 'B' ? T('side_after', 'Слушаем: с исполнением')
                                      : T('side_before', 'Слушаем: как записано');
    }
    if (state.playing) {
        const at = currentSec();
        stopSources();
        startAt(at);
    }
    if (window.__aeFunnel) window.__aeFunnel('ab_switch', { side });
}

function currentSec() {
    if (!state.playing || !state.ctx) return state.playStartSec;
    return state.playStartSec + (state.ctx.currentTime - state.playStartCtxTime);
}

async function togglePlay() {
    if (state.playing) { stopPlayback(); return; }
    if (!state.performed) return;

    const btn = $('mhPlay');
    try {
        if (!state.ctx) {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            state.ctx = new Ctx();
        }
        /* The click is the user gesture — unlock before any await, or Safari
           and Chrome-on-Android refuse the later start(). */
        if (state.ctx.state === 'suspended') await state.ctx.resume();

        if (!state.instruments) {
            if (btn) { btn.disabled = true; btn.textContent = T('loading_instruments', 'Загружаем инструменты…'); }
            await ensureInstruments(msg => { if (btn) btn.textContent = msg; });
            if (btn) btn.disabled = false;
        }
        startAt(state.playStartSec);
    } catch (err) {
        if (btn) { btn.disabled = false; btn.textContent = T('play', 'Слушать'); }
        if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'play_failed', err: String(err && err.message) });
        if (window.__handleError) window.__handleError(err, 'midi-humanizer/play', { say: T('err_audio', 'Не удалось запустить звук.') });
        else if (window.__toast) window.__toast(T('err_audio', 'Не удалось запустить звук.'), 'error');
    }
}

/**
 * Load the sample set once, shared by preview and WAV export.
 *
 * Instrument loading is the one heavy, network-bound step in the tool, and it
 * is where a killed tab leaves no trace at all — so the attempt is reported
 * with __aeNow (flushed immediately) BEFORE it starts, not after.
 */
async function ensureInstruments(setLabel) {
    if (state.instruments) return state.instruments;
    const programs = new Set(state.original.tracks.map(t => (t.channel === 9 ? 0 : t.program)));
    const notes = state.original.tracks.reduce((s, t) => s + t.notes.length, 0);
    if (window.__aeNow) window.__aeNow('tool_stage', { stage: 'instruments_begin', programs: programs.size, notes });

    const t0 = now();
    await ensureSoundfontLib();
    const label = T('loading_instruments', 'Загружаем инструменты…');
    const bases = soundfontBases((window.__MH_ASSETS || {}));
    state.instruments = await loadInstruments(state.ctx, state.original, (done, total) => {
        if (setLabel) setLabel(`${label} ${done}/${total}`);
    }, bases);

    const st = state.instruments.__stats || {};
    const ms = Math.round(now() - t0);
    if (window.__aeFunnel) window.__aeFunnel('instruments_ready', {
        ms, programs: st.programs, failed: st.failedPrograms || 0, fallback: st.usedFallback ? 1 : 0,
    });
    /* Every instrument failing is the mute-tool case: the page looks fine and
       plays nothing. It must not be silent in telemetry either. */
    if (st.programs && st.failedPrograms === st.programs) {
        if (window.__aeCritical) window.__aeCritical('instruments_all_failed', { programs: st.programs, bases: bases.length });
        if (window.__toast) window.__toast(T('err_instruments', 'Не удалось загрузить инструменты.'), 'error');
    } else if (st.failedPrograms) {
        if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'instruments_partial', failed: st.failedPrograms, of: st.programs });
    }
    return state.instruments;
}

function now() { return (window.performance && performance.now) ? performance.now() : Date.now(); }

function startAt(sec) {
    const doc = state.playSide === 'B' ? state.performed : state.original;
    let muted;
    if (state.soloTrack !== null && state.soloTrack !== undefined) {
        muted = new Set(doc.tracks.map(t => t.index).filter(i => i !== state.soloTrack));
    }
    /* Notes become nodes a second before they sound (NoteRender.live) — never a
       45 s window at once, and no rebuild every 40 s cutting what sounds. */
    const voices = liveVoices(doc, state.instruments, state.clock, {
        mutedTracks: muted,
        endSec: state.loop ? state.loop.endSec : Infinity,
        useExpression: true,
    });
    stopSources();
    state.live = window.NoteRender.live(state.ctx, voices);
    state.live.start(sec, state.ctx.currentTime + 0.1);
    /* `voices` keeps its meaning for the dashboard: notes due in the next 45 s. */
    let due = 0;
    voices.forEach(v => { if (!v.muted) v.notes.forEach(n => { if (n.time >= sec && n.time < sec + 45) due++; }); });
    state.playing = true;
    state.everPlayed = true;
    state.playStartSec = sec;
    state.playStartCtxTime = state.ctx.currentTime + 0.1;
    const btn = $('mhPlay');
    if (btn) btn.textContent = T('pause', 'Пауза');
    tick();
    if (window.__aeFunnel) window.__aeFunnel('play', { side: state.playSide, voices: due });
}

function tick() {
    cancelAnimationFrame(state.raf);
    const step = () => {
        if (!state.playing) return;
        const sec = currentSec();
        if (state.roll) {
            state.roll.setPlayhead(sec);
            const v = state.roll.view;
            if (sec > v.startSec + v.spanSec * 0.75 || sec < v.startSec) {
                state.roll.setView(sec - v.spanSec * 0.25, v.spanSec);
                syncScrollSlider();
            }
            state.roll.draw();
        }
        const pos = $('mhPos');
        if (pos) pos.textContent = fmtTime(sec);
        if (state.loop && sec >= state.loop.endSec) {
            stopSources(); startAt(state.loop.startSec); return;
        }
        state.raf = requestAnimationFrame(step);
    };
    state.raf = requestAnimationFrame(step);
}

function stopSources() {
    if (state.live) { try { state.live.dispose(); } catch (_) {} state.live = null; }
}
function stopPlayback() {
    if (!state.playing) { stopSources(); return; }
    const at = currentSec();
    stopSources();
    state.playing = false;
    state.playStartSec = at;
    cancelAnimationFrame(state.raf);
    const btn = $('mhPlay');
    if (btn) btn.textContent = T('play', 'Слушать');
}
function seekTo(sec) {
    const was = state.playing;
    stopPlayback();
    state.playStartSec = Math.max(0, sec);
    if (state.roll) { state.roll.setPlayhead(state.playStartSec); state.roll.draw(); }
    if (was) startAt(state.playStartSec);
}
function fmtTime(sec) {
    const s = Math.max(0, Math.floor(sec));
    return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}
function fmtDur(sec) {
    const v = Math.max(0, sec);
    return (v < 10 ? v.toFixed(1) : String(Math.round(v))) + T('sec_short', ' с');
}

/* ── export ───────────────────────────────────────────────────────────────── */

/**
 * The daily allowance is spent per FILE, and only for a result the user
 * actually receives. Loading a score, auditioning it, and crawling the diff are
 * all free — they cost the user nothing and us nothing, and charging for a
 * preview would make the counter meaningless. Exporting the same file again the
 * same day is also free: the key is the file, not the click.
 */
function beginRun() {
    if (!window.__toolLimit) return { commit() {}, abort() {} };
    const key = state.fileKey || null;
    const run = window.__toolLimit.begin('midi-humanizer', key ? [key] : null);
    return run;   /* null = the wall is up and the note is already on screen */
}

function exportMidi() {
    if (!state.performed) return;
    const run = beginRun();
    if (!run) return;
    try {
        const bytes = save(window.Midi, state.performed, { ccLanes: [1, 11] });
        const blob = new Blob([bytes], { type: 'audio/midi' });
        const base = state.fileName.replace(/\.(mid|midi|kar|rmi)$/i, '');
        window.__downloadFile(blob, base + ' — performed.mid');
        run.commit();
        if (window.__aeToolComplete) window.__aeToolComplete({
            output: 'midi',
            preset: state.settings.preset,
            strength: Math.round(state.settings.strength * 100),
            seed_changed: state.settings.seed !== 1 ? 1 : 0,
            latency: state.settings.libraryLatency,
            sep_channels: state.settings.separateChannels ? 1 : 0,
            cc: (state.settings.expressionMod ? 1 : 0) + (state.settings.expressionExpr ? 2 : 0),
            parts_edited: Object.keys(state.overrides).length,
            families_tuned: Object.keys(state.familyStrength).filter(f => state.familyStrength[f] !== 1).length,
            listened: state.everPlayed ? 1 : 0,
            notes: state.after.notes,
            bytes: blob.size,
        });
    } catch (err) {
        run.abort();
        if (window.__handleError) window.__handleError(err, 'midi-humanizer/export-midi');
    }
}

async function exportWav() {
    if (!state.performed) return;
    const btn = $('mhDownloadWav');
    const run = beginRun();
    if (!run) return;
    try {
        if (!state.instruments) {
            if (!state.ctx) {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                state.ctx = new Ctx();
            }
            if (state.ctx.state === 'suspended') await state.ctx.resume();
            if (btn) { btn.disabled = true; btn.textContent = T('loading_instruments', 'Загружаем инструменты…'); }
            await ensureInstruments(msg => { if (btn) btn.textContent = msg; });
        }
        if (btn) { btn.disabled = true; btn.textContent = T('rendering', 'Рендерим…'); }
        const seconds = Math.min(state.performed.durationSec, RENDER_LIMIT_SEC);
        if (window.__aeNow) window.__aeNow('tool_stage', { stage: 'wav_render_begin', seconds: Math.round(seconds) });
        const t0 = now();
        /* Export is the whole piece: soloing is an auditioning aid, and a WAV
           that silently contains one part would be a nasty surprise. */
        const blob = await renderWav(state.performed, state.instruments, state.clock, {
            startSec: 0, endSec: seconds, useExpression: true,
        });
        const base = state.fileName.replace(/\.(mid|midi|kar|rmi)$/i, '');
        window.__downloadFile(blob, base + ' — performed.wav');
        run.commit();
        if (window.__aeToolComplete) window.__aeToolComplete({
            output: 'wav', preset: state.settings.preset, strength: Math.round(state.settings.strength * 100),
            seconds: Math.round(seconds), truncated: state.performed.durationSec > RENDER_LIMIT_SEC ? 1 : 0,
            render_ms: Math.round(now() - t0), bytes: blob.size,
        });
    } catch (err) {
        run.abort();
        if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'wav_export_failed', err: String(err && err.message) });
        if (window.__handleError) window.__handleError(err, 'midi-humanizer/export-wav');
    } finally {
        if (btn) { btn.disabled = false; btn.textContent = T('download_wav', 'Скачать WAV'); }
    }
}

/* ── dom helpers ──────────────────────────────────────────────────────────── */

function show(id) { const el = $(id); if (el) el.hidden = false; }
function hide(id) { const el = $(id); if (el) el.hidden = true; }

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

/* Exposed for the E2E harness — the tests drive the same entry points a user
   does rather than a private copy of the logic. */
window.__MH = { state, recompute, onFile };
